// API client for auth/onboarding — uses AuthKit getAccessToken() for auth

import posthog from 'posthog-js'
import { getApiUrl } from '../../config/api-mode'
import { captureSessionId, debugDumpAuthState } from '../utils/session'

const API_URL = getApiUrl()

type GetAccessToken = () => Promise<string>
type AccountBlockedHandler = (error: unknown) => void
type SessionExpiredHandler = () => void

let _getAccessToken: GetAccessToken | null = null
let _onAccountBlocked: AccountBlockedHandler | null = null
let _onSessionExpired: SessionExpiredHandler | null = null
let _redirectingToLogout = false
let _lastSuccessfulTokenTime = 0
let _authKitFailedAt = 0

/** How long (ms) to skip AuthKit after a failure — go straight to refresh. */
const AUTHKIT_CIRCUIT_BREAKER_MS = 30_000

/** Paths that should not include tenant_slug — identity endpoints that return
 *  user data regardless of tenant context. */
const TENANT_SLUG_EXEMPT_PATHS = ['/api/users/me', '/api/auth/']

/** Max age (ms) of last successful token to trigger extended retries on reload. */
const RECENT_AUTH_WINDOW_MS = 30_000
const EXTENDED_RETRY_ATTEMPTS = 4
const EXTENDED_RETRY_DELAY_MS = 750

export function setAccessTokenGetter(fn: GetAccessToken) {
  _getAccessToken = fn
}

let _isRefreshing = false
let _refreshPromise: Promise<string | null> | null = null

/**
 * Refreshes the access token using the refresh token stored in localStorage.
 * Uses the backend /api/auth/refresh endpoint.
 */
async function refreshAccessToken(): Promise<string | null> {
  // Prevent concurrent refresh attempts
  if (_isRefreshing && _refreshPromise) {
    return _refreshPromise
  }

  _isRefreshing = true
  _refreshPromise = (async () => {
    const refreshToken = localStorage.getItem('refresh_token')
    if (!refreshToken) {
      return null
    }

    // Retry up to 5 times with exponential backoff for transient failures
    const MAX_REFRESH_RETRIES = 5
    for (let attempt = 0; attempt < MAX_REFRESH_RETRIES; attempt++) {
      try {
        const response = await fetch(`${API_URL}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        })

        if (response.ok) {
          const data = await response.json()
          if (data.access_token && data.refresh_token) {
            localStorage.setItem('access_token', data.access_token)
            localStorage.setItem('refresh_token', data.refresh_token)
            _lastSuccessfulTokenTime = Date.now()
            scheduleProactiveRefresh(data.access_token)
            return data.access_token
          }
        }

        // 401 = token genuinely invalid, stop retrying
        if (response.status === 401) {
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          return null
        }

        // Server error (500/503) — retry with exponential backoff, don't clear tokens
        if (attempt < MAX_REFRESH_RETRIES - 1) {
          const delay = 500 * Math.pow(2, attempt) // 500ms, 1s, 2s, 4s, 8s
          await new Promise(resolve => setTimeout(resolve, delay))
        }
      } catch {
        // Network error — retry with exponential backoff
        if (attempt < MAX_REFRESH_RETRIES - 1) {
          const delay = 500 * Math.pow(2, attempt)
          await new Promise(resolve => setTimeout(resolve, delay))
        }
      }
    }

    // All retries exhausted — report to PostHog
    posthog.capture('auth_token_refresh_exhausted', {
      attempts: MAX_REFRESH_RETRIES,
    })
    return null
  })()

  try {
    return await _refreshPromise
  } finally {
    _isRefreshing = false
    _refreshPromise = null
  }
}

// ── Proactive background refresh ────────────────────────────────
// Refresh the token ~5 minutes before it expires so no request ever
// hits an expired token. Timer is reset on every new token.
// Cross-tab: when one tab refreshes, others pick up the new token
// via the 'storage' event and reschedule their timers.
let _proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null

function scheduleProactiveRefresh(token: string): void {
  if (_proactiveRefreshTimer) {
    clearTimeout(_proactiveRefreshTimer)
    _proactiveRefreshTimer = null
  }

  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    if (!payload.exp) return

    const expiresAt = payload.exp * 1000
    const refreshAt = expiresAt - 5 * 60 * 1000 // 5 minutes before expiry
    const delay = refreshAt - Date.now()

    if (delay <= 0) return // Already past refresh window

    _proactiveRefreshTimer = setTimeout(async () => {
      _proactiveRefreshTimer = null
      const newToken = await refreshAccessToken()
      // If refresh returned null AND tokens were cleared (401 inside refresh),
      // the session is genuinely dead — redirect to logout.
      // If tokens still exist, it was a transient failure — do nothing.
      if (!newToken && !localStorage.getItem('access_token')) {
        handleSessionExpired()
      }
    }, delay)
  } catch {
    // Invalid token format — skip scheduling
  }
}

if (typeof window !== 'undefined') {
  // Clean up timer on tab close
  window.addEventListener('beforeunload', () => {
    if (_proactiveRefreshTimer) {
      clearTimeout(_proactiveRefreshTimer)
      _proactiveRefreshTimer = null
    }
  })

  // When another tab refreshes and writes a new access_token,
  // reschedule our timer with the fresh token instead of refreshing again
  window.addEventListener('storage', (event) => {
    if (event.key === 'access_token' && event.newValue) {
      scheduleProactiveRefresh(event.newValue)
    }
  })

  // Check token when user returns to a backgrounded tab
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      const storedToken = localStorage.getItem('access_token')
      if (storedToken) {
        try {
          const payload = JSON.parse(atob(storedToken.split('.')[1]))
          const expiresAt = payload.exp ? payload.exp * 1000 : 0
          const fiveMinutes = 5 * 60 * 1000
          if (expiresAt > 0 && expiresAt - Date.now() < fiveMinutes) {
            refreshAccessToken()
          }
        } catch {
          // Invalid token — ignore
        }
      }
    }
  })
}

export function clearProactiveRefresh(): void {
  if (_proactiveRefreshTimer) {
    clearTimeout(_proactiveRefreshTimer)
    _proactiveRefreshTimer = null
  }
}

function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.exp ? payload.exp * 1000 < Date.now() : false
  } catch {
    return true
  }
}

let _isResolving = false
let _resolvePromise: Promise<string | null> | null = null

async function resolveAccessToken(): Promise<string | null> {
  // Fast path: valid token in localStorage — no async work needed
  const storedToken = localStorage.getItem('access_token')
  if (storedToken && !isTokenExpired(storedToken)) {
    _lastSuccessfulTokenTime = Date.now()
    scheduleProactiveRefresh(storedToken)
    return storedToken
  }

  // Concurrency guard: if another call is already doing the heavy lifting
  // (AuthKit retries + refresh), piggyback on that result
  if (_isResolving && _resolvePromise) {
    return _resolvePromise
  }

  _isResolving = true
  _resolvePromise = (async () => {
    // If no stored token, try AuthKit — but skip if it recently failed
    // (circuit breaker: avoid re-attempting a known-broken AuthKit session)
    const authKitCircuitOpen =
      _authKitFailedAt > 0 &&
      Date.now() - _authKitFailedAt < AUTHKIT_CIRCUIT_BREAKER_MS

    if (_getAccessToken && !authKitCircuitOpen) {
      const wasRecentlyAuthenticated =
        _lastSuccessfulTokenTime > 0 &&
        Date.now() - _lastSuccessfulTokenTime < RECENT_AUTH_WINDOW_MS

      const maxAttempts = wasRecentlyAuthenticated ? EXTENDED_RETRY_ATTEMPTS : 2
      const delayMs = wasRecentlyAuthenticated ? EXTENDED_RETRY_DELAY_MS : 500
      const AUTHKIT_CALL_TIMEOUT_MS = 3000

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          const token = await Promise.race([
            _getAccessToken(),
            new Promise<null>((resolve) =>
              setTimeout(() => resolve(null), AUTHKIT_CALL_TIMEOUT_MS)
            ),
          ])
          if (token && !isTokenExpired(token)) {
            _lastSuccessfulTokenTime = Date.now()
            _authKitFailedAt = 0
            localStorage.setItem('access_token', token)
            scheduleProactiveRefresh(token)
            return token
          }
        } catch {
          if (attempt < maxAttempts - 1) {
            await new Promise((resolve) => setTimeout(resolve, delayMs))
          }
        }
      }

      // AuthKit failed — trip circuit breaker only if refresh token exists
      if (localStorage.getItem('refresh_token')) {
        _authKitFailedAt = Date.now()
      }
    }

    // AuthKit failed or skipped, try refreshing with stored refresh token
    if (localStorage.getItem('refresh_token')) {
      return refreshAccessToken()
    }

    return null
  })()

  try {
    return await _resolvePromise
  } finally {
    _isResolving = false
    _resolvePromise = null
  }
}

export async function getAccessTokenForRequest(): Promise<string | null> {
  return resolveAccessToken()
}

export function setAccountBlockedHandler(fn: AccountBlockedHandler | null) {
  _onAccountBlocked = fn
}

export function triggerAccountBlocked(error: unknown) {
  if (_onAccountBlocked) _onAccountBlocked(error)
}

export function resetAuthState() {
  _redirectingToLogout = false
  _authKitFailedAt = 0
}

export function setSessionExpiredHandler(fn: SessionExpiredHandler | null) {
  _onSessionExpired = fn
}

function handleSessionExpired(): void {
  if (_redirectingToLogout) return

  // During OAuth callback, token resolution is still in flight.
  // The CallbackPage handles auth errors itself via resolvePostLoginPath —
  // don't clear tokens or redirect, let the caller recover.
  if (window.location.pathname.startsWith('/auth/callback')) return

  // During intentional logout, don't override the reason with session_expired
  if (sessionStorage.getItem('intentional_logout')) return

  _redirectingToLogout = true

  clearProactiveRefresh()
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')

  if (_onSessionExpired) {
    _onSessionExpired()
  } else {
    window.location.href = '/auth/login'
  }
}

/**
 * Get current tenant slug from URL path.
 * Returns null when the path isn't under a tenant-slug route (e.g. /auth/*).
 *
 * Examples: /octopus/dashboard  -> "octopus"
 *           /british-gas/users  -> "british-gas"
 *           /auth/callback        -> null
 *           /featurebase/sso      -> null (Featurebase portal SSO page)
 */
const NON_TENANT_PREFIXES = ['auth', 'featurebase']

function getCurrentTenantSlug(): string | null {
  const path = window.location.pathname
  const segments = path.split('/').filter(Boolean)
  const first = segments[0]
  if (!first || NON_TENANT_PREFIXES.includes(first)) {
    return null
  }
  return first
}

interface ApiErrorResponse {
  status: number
  data: Record<string, unknown>
}

function isSessionAuthPayload(data: Record<string, unknown>): boolean {
  const code = typeof data.code === 'string' ? data.code : ''
  if (
    code === 'session_revoked' ||
    code === 'session_expired' ||
    code === 'token_expired' ||
    code === 'invalid_token' ||
    code === 'token_validation_failed' ||
    code === 'token_missing_subject'
  ) {
    return true
  }

  const detail = typeof data.detail === 'string' ? data.detail.toLowerCase() : ''
  return detail.includes('session') || detail.includes('token')
}

function isAccountBlockedPayload(data: Record<string, unknown>): boolean {
  if (data.code === 'account_blocked') return true

  const detail = typeof data.detail === 'string' ? data.detail.toLowerCase() : ''
  if (!detail) return false

  return (
    detail.includes('deactivated') ||
    detail.includes('has been deleted') ||
    detail.includes('no longer active') ||
    detail.includes('organisation') ||
    detail.includes('organization')
  )
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  isRetry = false,
): Promise<T> {
  const isFormData = options.body instanceof FormData
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers as Record<string, string> || {}),
  }

  // Attach auth token — if a getter is registered (user is logged in) but
  // token resolution fails, skip the request entirely instead of sending it
  // without auth (which would trigger a 401 → forced logout).
  const token = await resolveAccessToken()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  } else if (_getAccessToken || localStorage.getItem('refresh_token')) {
    // Token getter exists but returned null — transient refresh failure.
    // Throw so callers can retry; don't send an unauthenticated request.
    throw { status: 0, data: { code: 'token_unavailable' } } as ApiErrorResponse
  }

  // Add tenant_slug to query params for multi-tenancy
  // Skip for non-tenant routes (e.g. /auth/*) and identity endpoints (e.g. /api/users/me)
  const tenantSlug = getCurrentTenantSlug()
  let url = `${API_URL}${path}`
  const skipSlug = TENANT_SLUG_EXEMPT_PATHS.some((p) => path.startsWith(p))
  if (tenantSlug && !skipSlug) {
    const separator = path.includes('?') ? '&' : '?'
    url += `${separator}tenant_slug=${tenantSlug}`
  }

  const res = await fetch(url, {
    ...options,
    headers,
  })

  if (!res.ok) {
    let data: Record<string, unknown> = {}
    try {
      data = await res.json()
    } catch {
      // Non-JSON response
    }

    const error: ApiErrorResponse = { status: res.status, data }

    // Report 5xx server errors to PostHog for error tracking
    if (res.status >= 500) {
      posthog.capture('api_error', {
        status: res.status,
        path: path,
        method: options.method || 'GET',
        error_code: (data as Record<string, unknown>)?.error?.code || 'UNKNOWN_ERROR',
      })
    }

    // No-tenant check — user is authenticated but has no organisation.
    // Handled identically to account_blocked: redirect to logout to clear
    // the WorkOS session, then show an error banner on the login page.
    // DRF auth exceptions: {"detail": "...", "code": "NO_TENANT"}
    // TenantMiddleware:    {"error": {"code": "NO_TENANT"}}
    if (
      res.status === 403 &&
      (data.code === 'NO_TENANT' ||
        (data.error && (data.error as Record<string, unknown>).code === 'NO_TENANT'))
    ) {
      if (!_redirectingToLogout) {
        _redirectingToLogout = true
        console.warn('[auth:api-client] NO_TENANT — capturing session_id before clearing tokens')
        debugDumpAuthState('api-client NO_TENANT before clear')
        captureSessionId()
        sessionStorage.setItem('logout_reason', 'no_tenant')
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        debugDumpAuthState('api-client NO_TENANT after clear')
        window.location.href = '/auth/error'
      }
      return new Promise<T>(() => {})
    }

    // Account-blocked check — more specific than generic 401/session
    // checks. Prevents a blocked account from being misrouted as session expiry.
    if (res.status === 403 && !path.includes('/auth/signout')) {
      if (isAccountBlockedPayload(data)) {
        triggerAccountBlocked(error)
        return new Promise<T>(() => {})
      }
    }

    // Auth/session failures — try to refresh token once before giving up
    if ((res.status === 401 || isSessionAuthPayload(data)) && !isRetry) {
      const refreshToken = localStorage.getItem('refresh_token')
      if (refreshToken && !path.includes('/auth/')) {
        const newToken = await refreshAccessToken()
        if (newToken) {
          return request<T>(path, options, true)
        }
      }

      // No refresh token or refresh failed — redirect to login
      handleSessionExpired()
      throw error
    }

    throw error
  }

  // Handle 204 No Content
  if (res.status === 204) {
    return undefined as T
  }

  return res.json()
}

export const apiClient = {
  get<T>(path: string, options: RequestInit = {}): Promise<T> {
    return request<T>(path, { ...options, method: 'GET' })
  },

  post<T>(path: string, body?: unknown, options: RequestInit = {}): Promise<T> {
    return request<T>(path, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    })
  },

  patch<T>(path: string, body?: unknown, options: RequestInit = {}): Promise<T> {
    return request<T>(path, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    })
  },

  delete<T>(path: string, options: RequestInit = {}): Promise<T> {
    return request<T>(path, { ...options, method: 'DELETE' })
  },

  upload<T>(path: string, formData: FormData, options: RequestInit = {}): Promise<T> {
    return request<T>(path, {
      ...options,
      method: 'POST',
      body: formData,
    })
  },
}
